import { Info } from '@mui/icons-material';
import { Typography } from '@mui/material';
import { getArrearsComiteeReport } from 'lib/api/collections';
import { ArrearsComiteeDocument } from 'lib/api/collections/types';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import { DocumentProfileContext } from 'lib/context/DocumentProfileProvider';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions from 'lib/designSystem/HeaderWithActions';
import EnhancedTable from 'lib/designSystem/Table';
import CustomToggleButtonGroup from 'lib/designSystem/ToggleButtonGroup';
import { showAlert } from 'lib/functions/utils';
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

const groupByBusinessName = (
  documents: ArrearsComiteeDocument[],
): Record<string, ArrearsComiteeDocument[]> => {
  return documents.reduce(
    (acc, document) => {
      const businessName = document.businessName || 'Sin Cliente';
      if (!acc[businessName]) {
        acc[businessName] = [];
      }
      acc[businessName].push(document);
      return acc;
    },
    {} as Record<string, ArrearsComiteeDocument[]>,
  );
};

const ArrearsComitee: React.FC = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ArrearsComiteeDocument[]>([]);
  const [filteredData, setFilteredData] = useState<ArrearsComiteeDocument[]>(
    [],
  );
  const [sectionedData, setSectionedData] = useState<
    Record<string, ArrearsComiteeDocument[]>
  >({});
  const [collectionStatesOptions, setCollectionStatesOptions] = useState<
    string[]
  >([]);
  const [collectionOptionOptions, setCollectionOptionOptions] = useState<
    string[]
  >([]);
  const [collectorOptions, setCollectorOptions] = useState<string[]>([]);
  const [commercialExecutiveOptions, setCommercialExecutiveOptions] = useState<
    string[]
  >([]);
  const [businessNameOptions, setBusinessNameOptions] = useState<string[]>([]);
  const [stakeholderNameOptions, setStakeholderNameOptions] = useState<
    string[]
  >([]);

  const [collectionState, setCollectionState] = useState<string>('');
  const [collectionOption, setCollectionOption] = useState<string>('');
  const [collector, setCollector] = useState<string>('');
  const [commercialExecutive, setCommercialExecutive] = useState<string>('');
  const [businessIdentifier, setBusinessIdentifier] = useState<string>('');
  const [businessName, setBusinessName] = useState<string>('');
  const [stakeholderIdentifier, setStakeholderIdentifier] =
    useState<string>('');
  const [stakeholderName, setStakeholderName] = useState<string>('');
  const [folio, setFolio] = useState<string>('');
  const [status, setStatus] = useState<string>('Todos');

  const [showSectioned, setShowSectioned] = useState<boolean>(false);

  const { onOpenDocumentProfile } = useContext(DocumentProfileContext);

  const tableHeaders = [
    { key: 'documentType', label: 'Tipo Documento' },
    { key: 'siiDescription', label: 'Descripción SII' },
    { key: 'businessIdentifier', label: 'Rut Cliente' },
    { key: 'businessName', label: 'Cliente' },
    { key: 'stakeholderIdentifier', label: 'Rut Deudor' },
    { key: 'stakeholderName', label: 'Deudor' },
    {
      key: 'folio',
      label: 'Folio',
      handleAction: onOpenDocumentProfile,
      icon: <Info fontSize="small" />,
    },
    { key: 'amount', label: 'Monto', type: 'money' },
    { key: 'amountConciliated', label: 'Conciliado', type: 'money' },
    { key: 'creditNoteAmount', label: 'Monto nota crédito', type: 'money' },
    {
      key: 'leftAmountNet',
      label: 'Adeudado Doc.',
      type: 'invoice-remaining-amount',
    },
    {
      key: 'issuedDate',
      label: 'Fecha Emisión',
      type: 'eight-days-date',
    },
    { key: 'paymentDate', label: 'Fecha vencimiento', type: 'date' },
    {
      key: 'originalPaymentDate',
      label: 'Fecha Vencimiento Original',
      type: 'date',
    },
    { key: 'arrearDays', label: 'Días Mora' },
    { key: 'lastCollectionManagementStatus', label: 'E. Cobranza' },
    { key: 'lastCollectionManagementOption', label: 'S. Cobranza' },
    {
      key: 'lastCollectionManagementObservation',
      label: 'O. Cobranza',
    },
    { key: 'commercialExecutiveName', label: 'Ejecutivo Comercial' },
    { key: 'collectorName', label: 'Ejecutivo Cobranza' },
    { key: 'fundName', label: 'Fondo' },
  ];

  const sectionedHeaders = [
    { key: 'documentType', label: 'Tipo Documento' },
    { key: 'stakeholderIdentifier', label: 'Rut Deudor' },
    { key: 'stakeholderName', label: 'Deudor' },
    {
      key: 'folio',
      label: 'Folio',
      handleAction: onOpenDocumentProfile,
      icon: <Info fontSize="small" />,
    },
    { key: 'amount', label: 'Monto', type: 'money' },
    { key: 'creditNoteAmount', label: 'Monto nota crédito', type: 'money' },
    {
      key: 'leftAmountNet',
      label: 'Adeudado Doc.',
      type: 'invoice-remaining-amount',
    },
    { key: 'paymentDate', label: 'Fecha vencimiento', type: 'date' },
    { key: 'arrearAmount', label: 'Interés Mora', type: 'money' },
    { key: 'arrearDays', label: 'Días Mora' },
    {
      key: 'lastCollectionManagementObservation',
      label: 'O. Cobranza',
    },
    { key: 'fundName', label: 'Fondo' },
    { key: 'siiDescription', label: 'Descripción SII' },
  ];

  const filters: Filter[] = [
    {
      label: 'Estado',
      type: 'localAutocomplete',
      value: status,
      setter: setStatus,
      options: [
        { label: 'En mora', value: 'En mora' },
        { label: 'Vigente', value: 'Vigente' },
        { label: 'Todos', value: 'Todos' },
      ],
    },
    {
      label: 'E. Cobranza',
      type: 'localAutocomplete',
      value: collectionState,
      setter: setCollectionState,
      options: collectionStatesOptions.map((state) => ({
        label: state,
        value: state,
      })),
    },
    {
      label: 'S. Cobranza',
      type: 'localAutocomplete',
      value: collectionOption,
      setter: setCollectionOption,
      options: collectionOptionOptions.map((option) => ({
        label: option,
        value: option,
      })),
    },
    {
      label: 'Nombre Cliente',
      type: 'localAutocomplete',
      value: businessName,
      setter: setBusinessName,
      options: businessNameOptions.map((name) => ({
        label: name,
        value: name,
      })),
    },
    {
      label: 'Rut Cliente',
      type: 'text',
      value: businessIdentifier,
      setter: setBusinessIdentifier,
    },
    {
      label: 'Nombre Deudor',
      type: 'localAutocomplete',
      value: stakeholderName,
      setter: setStakeholderName,
      options: stakeholderNameOptions.map((name) => ({
        label: name,
        value: name,
      })),
    },
    {
      label: 'Rut Deudor',
      type: 'text',
      value: stakeholderIdentifier,
      setter: setStakeholderIdentifier,
    },
    {
      label: 'Folio',
      type: 'text',
      value: folio,
      setter: setFolio,
    },
    {
      label: 'Ejecutivo Cobranza',
      type: 'localAutocomplete',
      value: collector,
      setter: setCollector,
      options: collectorOptions.map((option) => ({
        label: option,
        value: option,
      })),
    },
    {
      label: 'Ejecutivo Comercial',
      type: 'localAutocomplete',
      value: commercialExecutive,
      setter: setCommercialExecutive,
      options: commercialExecutiveOptions.map((option) => ({
        label: option,
        value: option,
      })),
    },
  ];

  const fetchData = async () => {
    const response = await getArrearsComiteeReport(dispatch);
    if (!response) throw new Error('Error al obtener los datos');
    const documents = [...response.documents];
    setData(documents);
    setFilteredData(documents);
    setSectionedData(groupByBusinessName(documents));
    setCollectionStatesOptions(response.collectionStatesOptions);
    setCollectionOptionOptions(response.collectionOptionOptions);
    setCollectorOptions(response.collectorOptions);
    setCommercialExecutiveOptions(response.commercialExecutiveOptions);
    setBusinessNameOptions(response.businessNameOptions);
    setStakeholderNameOptions(response.stakeholderNameOptions);
    return response;
  };

  const triggerFilter = async () => {
    let newFilteredData = [...data];
    if (folio)
      newFilteredData = newFilteredData.filter(
        (document) => document.folio === folio,
      );
    if (businessIdentifier)
      newFilteredData = newFilteredData.filter(
        (document) => document.businessIdentifier === businessIdentifier,
      );
    if (businessName)
      newFilteredData = newFilteredData.filter(
        (document) => document.businessName === businessName,
      );
    if (stakeholderIdentifier)
      newFilteredData = newFilteredData.filter(
        (document) => document.stakeholderIdentifier === stakeholderIdentifier,
      );
    if (stakeholderName)
      newFilteredData = newFilteredData.filter(
        (document) => document.stakeholderName === stakeholderName,
      );
    if (collectionState)
      newFilteredData = newFilteredData.filter(
        (document) =>
          document.lastCollectionManagementStatus === collectionState,
      );
    if (collectionOption)
      newFilteredData = newFilteredData.filter(
        (document) =>
          document.lastCollectionManagementOption === collectionOption,
      );
    if (collector)
      newFilteredData = newFilteredData.filter(
        (document) => document.collectorName === collector,
      );
    if (commercialExecutive)
      newFilteredData = newFilteredData.filter(
        (document) => document.commercialExecutiveName === commercialExecutive,
      );
    if (status === 'En mora')
      newFilteredData = newFilteredData.filter(
        (document) => document.arrearDays > 0,
      );
    if (status === 'Vigente')
      newFilteredData = newFilteredData.filter(
        (document) => document.arrearDays === 0,
      );
    setFilteredData([...newFilteredData]);
    setSectionedData(groupByBusinessName(newFilteredData));
  };

  const setUp = async () => {
    setLoading(true);
    try {
      await fetchData();
    } catch (error) {
      console.error(error);
      showAlert(dispatch, 'error', 'Error al obtener los datos');
    }
    setLoading(false);
  };

  useEffect(() => {
    void setUp();
  }, []);

  if (loading) return <CenteredCircularProgress type="layout" />;

  return (
    <div>
      <HeaderWithActions
        variant="body1"
        title="Acá encontrarás todos los documentos activos para comité de mora."
        buttons={[
          {
            name: 'CustomToggleButtonGroup',
            action: () => {},
            color: 'primary',
            customButton: (
              <CustomToggleButtonGroup
                options={[
                  { label: 'Tabla', value: 'tabla' },
                  { label: 'Seccionado', value: 'seccionado' },
                ]}
                value={showSectioned ? 'seccionado' : 'tabla'}
                onChange={(value) => setShowSectioned(value === 'seccionado')}
              />
            ),
          },
          {
            name: 'Filtrar',
            action: () => {},
            color: 'primary',
            customButton: (
              <GlobalSearcher
                filters={filters}
                triggerSearch={triggerFilter}
                dispatch={dispatch}
                disableSearch={loading}
              />
            ),
          },
        ]}
      />

      {!showSectioned && (
        <EnhancedTable
          dataHeaders={tableHeaders}
          data={filteredData}
          rowsPerPageDefault={25}
          lastElement
          exportToFile="xlsx"
        />
      )}
      {showSectioned && (
        <div>
          {Object.entries(sectionedData).map(
            ([businessName, documents], index, array) => (
              <div key={businessName} style={{ marginBottom: '5px' }}>
                <Typography variant="h6" fontWeight="bold">
                  {businessName}
                </Typography>
                <EnhancedTable
                  dataHeaders={sectionedHeaders}
                  data={documents}
                  rowsPerPageDefault={5}
                  lastElement={index === array.length - 1}
                  exportToFile="xlsx"
                />
              </div>
            ),
          )}
        </div>
      )}
    </div>
  );
};

export default ArrearsComitee;
